import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpService } from '../http/http.service';
import { JobService } from '../job/job.service';
import { Job } from '../job/job';
import {
  ApiResponseTournamentData,
  ApiResponsePublishedTournaments,
  WagerApi,
  ApiResponseMiniLeaderboardData,
  ApiResponseTournamentLeaderboardList,
  ApiResponseTournamentLeaderboardEntry,
  ApiResponseRoomTournaments,
  ApiResponseFindMatch,
  TournamentView,
  PublishedTournament,
  LeaderBoardEntry,
} from '../../api/wager-api';
import {
  getMockPublishedTournaments,
  getMockTournamentById,
  getMockMiniLeaderboard,
  getMockLeaderboardList,
  getMockRoomTournaments,
} from './tournament.mock';
import { CurrentlySupportedGames, GameId } from 'src/app/model/game/game-id';
import { getOrdinalSuffix } from 'src/app/shared/utils/tournament.utils';
import { TranslocoService } from '@jsverse/transloco';
import { TournamentStatus } from 'src/app/shared/types/tournament';
import {
  BehaviorSubject,
  from,
  interval,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

export type TournamentData = {
  tournament: TournamentView;
  entries: LeaderBoardEntry[];
  gameId: GameId;
  status: TournamentStatus;
};

@Injectable({
  providedIn: 'root',
})
export class TournamentService implements OnDestroy {
  private httpService = inject(HttpService);
  private jobService = inject(JobService);
  private translocoService = inject(TranslocoService);

  private readonly useMockData = false;
  private readonly mockDelay = 1000;
  private readonly tournamentIntervalDuration = 30000;

  private allCurrentTournaments = new BehaviorSubject<TournamentData[]>([]);
  private liveTournaments = new BehaviorSubject<Partial<Record<GameId, TournamentData>>>({});
  private pollingRefCount = 0;
  private pollingStop$ = new Subject<void>();
  private pollingSubscription?: Subscription;
  private isInitial = true;

  public get allCurrentTournaments$(): Observable<TournamentData[]> {
    return this.allCurrentTournaments.asObservable();
  }
  public get liveTournaments$(): Observable<Partial<Record<GameId, TournamentData>>> {
    return this.liveTournaments.asObservable();
  }

  startTournamentPolling(): void {
    // TODO: Disable tournaments for now
    return;

    this.pollingRefCount++;

    // Already polling, just increase the count
    if (this.pollingSubscription) {
      return;
    }

    interval(this.tournamentIntervalDuration)
      .pipe(
        startWith(0),
        takeUntil(this.pollingStop$),
        switchMap(() => from(this.getTournaments(this.isInitial))),
        tap(() => {
          this.isInitial = false;
        }),
      )
      .subscribe((payload: ApiResponsePublishedTournaments) => {
        if (!payload?.data?.length) {
          this.allCurrentTournaments.next([]);
          this.liveTournaments.next({});
          return;
        }

        const sortedTournaments = sortRawPublishedTournaments(payload.data);
        this.allCurrentTournaments.next(sortedTournaments);
        this.liveTournaments.next(
          getLiveTournamentsForGames(sortedTournaments, CurrentlySupportedGames),
        );
      });
  }

  stopTournamentPolling(): void {
    this.pollingRefCount--;

    if (this.pollingRefCount <= 0) {
      this.forceStopPolling();
    }
  }

  ngOnDestroy(): void {
    this.forceStopPolling();
  }

  private forceStopPolling(): void {
    this.pollingStop$.next();
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.pollingRefCount = 0;
  }

  static getTournamentStatus(tournament: TournamentView): TournamentStatus {
    const now = new Date().getTime();
    if (tournament.finished) {
      return TournamentStatus.Finished;
    } else if (now > tournament.joinUntilDate) {
      return TournamentStatus.CollectingResults;
    } else if (now > tournament.endDate) {
      return TournamentStatus.JoinOnly;
    } else if (now > tournament.startDate) {
      return TournamentStatus.Live;
    }
    return TournamentStatus.NotStarted;
  }

  private async getTournaments(isInitial?: boolean): Promise<ApiResponsePublishedTournaments> {
    const job = this.getPublishedTournaments();
    job.showNoInternetRetry();
    if (isInitial) job.showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    return await job.run();
  }

  private getPublishedTournaments(): Job<ApiResponsePublishedTournaments> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) =>
            setTimeout(() => resolve(getMockPublishedTournaments()), this.mockDelay),
          ),
      );
    }

    return this.jobService.create(() => WagerApi.request(this.httpService).publishedTournaments());
  }

  getTournamentById(tournamentId: string): Job<ApiResponseTournamentData> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) =>
            setTimeout(() => resolve(getMockTournamentById(tournamentId)), this.mockDelay),
          ),
      );
    }

    return this.jobService.create(() =>
      WagerApi.request(this.httpService).tournamentData(tournamentId),
    );
  }

  getMiniLeaderboard(tournamentId: string): Job<ApiResponseMiniLeaderboardData> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) =>
            setTimeout(() => resolve(getMockMiniLeaderboard(tournamentId)), this.mockDelay),
          ),
      );
    }

    return this.jobService.create(() =>
      WagerApi.request(this.httpService).miniLeaderboardData(tournamentId),
    );
  }

  getLeaderboardList(
    tournamentId: string,
    page: number,
  ): Job<ApiResponseTournamentLeaderboardList> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) =>
            setTimeout(() => resolve(getMockLeaderboardList(tournamentId, page)), this.mockDelay),
          ),
      );
    }

    return this.jobService.create(() =>
      WagerApi.request(this.httpService).leaderboardList(tournamentId, page),
    );
  }

  getLeaderboardEntry(tournamentId: string): Job<ApiResponseTournamentLeaderboardEntry> {
    return this.jobService.create(() =>
      WagerApi.request(this.httpService).leaderboardEntry(tournamentId),
    );
  }

  getRoomTournaments(roomId: string): Job<ApiResponseRoomTournaments> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) =>
            setTimeout(() => resolve(getMockRoomTournaments()), this.mockDelay),
          ),
      );
    }

    return this.jobService.create(() => WagerApi.request(this.httpService).roomTournaments(roomId));
  }

  findTournamentMatch(
    gameId: GameId,
    tierId: string,
    depositId: string,
    tournamentId: string,
  ): Job<ApiResponseFindMatch> {
    if (this.useMockData) {
      return this.jobService.create(() =>
        WagerApi.request(this.httpService).findMatch(gameId, tierId, depositId),
      );
    }

    return this.jobService.create(() =>
      WagerApi.request(this.httpService).findMatch(gameId, tierId, depositId, tournamentId),
    );
  }

  calTopScorePlaceholder(rank: number): string {
    const suffix = getOrdinalSuffix(rank);
    return this.translocoService.translate(`pages.tournament.top-score-placeholder`, {
      rank: `${rank}${suffix}`,
    });
  }

  // navigationChanged(url: string): void {
  //   if (url === `/${PAGE.CHOOSE_GAME}`) {
  //     this.startTournamentCardTimer();
  //   } else {
  //     this.stopTournamentCardTimer();
  //   }
  // }
}

function sortRawPublishedTournaments(data: PublishedTournament[]): TournamentData[] {
  const statusOrder: Record<TournamentStatus, number> = {
    [TournamentStatus.Live]: 0,
    [TournamentStatus.JoinOnly]: 1,
    [TournamentStatus.CollectingResults]: 2,
    [TournamentStatus.NotStarted]: 3,
    [TournamentStatus.Finished]: 4,
  };
  return data
    .map((item) => {
      const { tournament, entries } = item;
      return {
        tournament,
        entries,
        gameId: tournament.gameIds?.[0] ?? GameId.Solitaire,
        status: TournamentService.getTournamentStatus(tournament),
      };
    })
    .sort((a, b) => {
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) {
        return statusDiff;
      }
      return a.tournament.endDate - b.tournament.endDate;
    });
}

function getLiveTournamentsForGames(
  tournaments: TournamentData[],
  gameIds: GameId[],
): Partial<Record<GameId, TournamentData>> {
  const targetGameIds = new Set(gameIds);
  const result: Partial<Record<GameId, TournamentData>> = {};

  for (const tournament of tournaments) {
    const { gameId, status } = tournament;

    if (
      (status === TournamentStatus.Live || status === TournamentStatus.JoinOnly) &&
      targetGameIds.has(gameId) &&
      !result[gameId]
    ) {
      result[gameId] = tournament;
    }

    // Early exit if all gameIds have been matched
    if (Object.keys(result).length === gameIds.length) {
      break;
    }
  }

  return result;
}
